import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SchoolDetailsModal from '../../components/SchoolDetailsModal';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import {
  ArrowLeft,
  Shield,
  Users,
  Award,
  Star,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import {
  ComparisonBarChart,
  DistributionPieChart,
  StatCard,
} from '../../components/analytics';
import { selectTotalSchools } from '../../../../../store/slices/pmShriConstantsSlice';
import {
  useGetSamagamNCCProfileDetailsQuery,
  type NCCProfileDetailsResponse,
  type NCCSchoolRecord,
} from '../../services/kvsSamagamApi';

// ─── helpers ────────────────────────────────────────────────────────────────

const EMPTY_NCC_STATE: any = {
  summary:      { totalWithNCC: 0, totalArmy: 0, totalNavy: 0, totalAirForce: 0, totalOfficers: 0 },
  availability: { pmshri: 0, nonPmshri: 0 },
  groups:       { army: { pmshri: 0, nonPmshri: 0 }, navy: { pmshri: 0, nonPmshri: 0 }, airForce: { pmshri: 0, nonPmshri: 0 } },
  wings:        { juniorDivision: { pmshri: 0, nonPmshri: 0 }, seniorDivision: { pmshri: 0, nonPmshri: 0 } },
  officers:     { ano: { total: 0, pmshri: 0, nonPmshri: 0 }, cto: { total: 0, pmshri: 0, nonPmshri: 0 } },
  ranks:        {},
  topSchools:   { all: [], byGroup: { army: [], navy: [], airForce: [] }, byWing: { juniorDivision: [], seniorDivision: [] }, byOfficer: { ano: [], cto: [] } },
};

/**
 * Count PM-SHRI / Non-PM-SHRI from a pre-filtered school list.
 */
function countByType(list: NCCSchoolRecord[]) {
  return {
    pmshri:    list.filter(s => s.isPMShree).length,
    nonPmshri: list.filter(s => !s.isPMShree).length,
  };
}

/**
 * Shape a school record into the display object used across all tabs.
 */
function toTopSchool(s: NCCSchoolRecord) {
  return {
    schoolID:    s.schoolID,
    schoolName:  s.schoolName,
    location:    s.roName,
    status:      s.isPMShree ? 'PM-SHRI' : 'Non PM-SHRI',
    nccScore:    s.totalCadets,
    nccGroup:    s.nccGroup,
    nccWing:     s.avaliableWing,
    officer:     s.anoCto || '-',
    totalCadets: s.totalCadets,
  };
}

/**
 * Derive full component state from a single API payload.
 * One endpoint provides all data required across all four tabs.
 */
function transformApiData(apiData: NCCProfileDetailsResponse): any {
  const { summary, schools } = apiData;

  // ── Groups ──────────────────────────────────────────────────────────────
  // nccGroup values: 'Army' | 'Naval' | 'Air Force' (case-insensitive guard)
  const armySchools  = schools.filter(s => s.nccGroup?.toLowerCase().includes('army'));
  const navySchools  = schools.filter(s => s.nccGroup?.toLowerCase().includes('nav'));  // 'Naval'
  const afSchools    = schools.filter(s => s.nccGroup?.toLowerCase().includes('air'));

  // ── Wings ───────────────────────────────────────────────────────────────
  // avaliableWing values: 'Sr. Wing' | 'Jr. Wing'
  const wingLC    = (s: NCCSchoolRecord) => (s.avaliableWing ?? '').toLowerCase();
  const jdSchools = schools.filter(s => wingLC(s).includes('jr'));
  const sdSchools = schools.filter(s => wingLC(s).includes('sr'));

  // ── Officers ────────────────────────────────────────────────────────────
  // anoCto field: 'ANO' | 'CTO'
  const anoSchools = schools.filter(s => s.anoCto === 'ANO');
  const ctoSchools = schools.filter(s => s.anoCto === 'CTO');

  // ── Top-schools sort helper ─────────────────────────────────────────────
  const top10 = (list: NCCSchoolRecord[]) =>
    [...list].sort((a, b) => b.totalCadets - a.totalCadets).slice(0, 10).map(toTopSchool);

  return {
    summary: {
      totalWithNCC:  summary.nccAvailable,
      totalArmy:     summary.armyWingUnits,
      totalNavy:     summary.navyWingUnits,
      totalAirForce: summary.airForceWingUnits,
      totalOfficers: summary.totalNccOfficers,
    },
    availability: {
      pmshri:    summary.pmShri,
      nonPmshri: summary.nonPmShri,
    },
    groups: {
      army:     countByType(armySchools),
      navy:     countByType(navySchools),
      airForce: countByType(afSchools),
    },
    wings: {
      juniorDivision: countByType(jdSchools),
      seniorDivision: countByType(sdSchools),
    },
    officers: {
      ano: { total: summary.anoCount, ...countByType(anoSchools) },
      cto: { total: summary.ctoCount, ...countByType(ctoSchools) },
    },
    ranks: {},
    topSchools: {
      all: top10(schools),
      byGroup: {
        army:     top10(armySchools),
        navy:     top10(navySchools),
        airForce: top10(afSchools),
      },
      byWing: {
        juniorDivision: top10(jdSchools),
        seniorDivision: top10(sdSchools),
      },
      byOfficer: {
        ano: top10(anoSchools),
        cto: top10(ctoSchools),
      },
    },
  };
}

export default function NCCProfileAnalysis() {
  const navigate = useNavigate();
  const totalSchoolsFromRedux = useSelector(selectTotalSchools);
  const [activeTab, setActiveTab] = useState<'availability' | 'groups' | 'wings' | 'officers'>('availability');
  const [data, setData] = useState<any>(EMPTY_NCC_STATE);
  const [selectedSchool, setSelectedSchool] = useState<{
    schoolID: number;
    schoolName: string;
    infraData?: any;
    tabLabel?: string;
  } | null>(null);

  // Single call — all four tabs are derived from this one endpoint
  const { data: nccApiData, isLoading } = useGetSamagamNCCProfileDetailsQuery(
    { category: 'nccavailability' }
  );

  // Transform once when API data arrives; no tab dependency needed
  useEffect(() => {
    if (nccApiData) {
      setData(transformApiData(nccApiData));
    }
  }, [nccApiData]);

  if (!data) return null;

  const tabs = [
    { id: 'availability', label: 'NCC Availability', icon: Shield },
    { id: 'groups', label: 'NCC Groups', icon: Users },
    { id: 'wings', label: 'NCC Wings', icon: Award },
    { id: 'officers', label: 'Officers (ANO/CTO)', icon: Star },
  ];

  const addPercentage = (items: { category: string; pmshriCount: number; nonPmshriCount: number; total: number }[]) =>
    items.map(item => ({
      ...item,
      pmshriPercentage: item.total > 0 ? (item.pmshriCount / item.total) * 100 : 0
    }));

  const getComparisonData = () => {
    if (!data) return [];
    
    switch (activeTab) {
      case 'availability':
        return addPercentage([
          { 
            category: 'Schools with NCC', 
            pmshriCount: data?.availability?.pmshri || 0, 
            nonPmshriCount: data?.availability?.nonPmshri || 0, 
            total: (data?.availability?.pmshri || 0) + (data?.availability?.nonPmshri || 0) 
          },
        ]);
      case 'groups':
        return addPercentage([
          { 
            category: 'Army', 
            pmshriCount: data?.groups?.army?.pmshri || 0, 
            nonPmshriCount: data?.groups?.army?.nonPmshri || 0, 
            total: (data?.groups?.army?.pmshri || 0) + (data?.groups?.army?.nonPmshri || 0) 
          },
          { 
            category: 'Navy', 
            pmshriCount: data?.groups?.navy?.pmshri || 0, 
            nonPmshriCount: data?.groups?.navy?.nonPmshri || 0, 
            total: (data?.groups?.navy?.pmshri || 0) + (data?.groups?.navy?.nonPmshri || 0) 
          },
          { 
            category: 'Air Force', 
            pmshriCount: data?.groups?.airForce?.pmshri || 0, 
            nonPmshriCount: data?.groups?.airForce?.nonPmshri || 0, 
            total: (data?.groups?.airForce?.pmshri || 0) + (data?.groups?.airForce?.nonPmshri || 0) 
          },
        ]);
      case 'wings':
        return addPercentage([
          { 
            category: 'Junior Division', 
            pmshriCount: data?.wings?.juniorDivision?.pmshri || 0, 
            nonPmshriCount: data?.wings?.juniorDivision?.nonPmshri || 0, 
            total: (data?.wings?.juniorDivision?.pmshri || 0) + (data?.wings?.juniorDivision?.nonPmshri || 0) 
          },
          { 
            category: 'Senior Division', 
            pmshriCount: data?.wings?.seniorDivision?.pmshri || 0, 
            nonPmshriCount: data?.wings?.seniorDivision?.nonPmshri || 0, 
            total: (data?.wings?.seniorDivision?.pmshri || 0) + (data?.wings?.seniorDivision?.nonPmshri || 0) 
          },
        ]);
      case 'officers':
        return addPercentage([
          { 
            category: 'ANO (Associated NCC Officer)', 
            pmshriCount: data?.officers?.ano?.pmshri || 0, 
            nonPmshriCount: data?.officers?.ano?.nonPmshri || 0, 
            total: data?.officers?.ano?.total || 0 
          },
          { 
            category: 'CTO (Caretaker Officer)', 
            pmshriCount: data?.officers?.cto?.pmshri || 0, 
            nonPmshriCount: data?.officers?.cto?.nonPmshri || 0, 
            total: data?.officers?.cto?.total || 0 
          },
        ]);
      default:
        return [];
    }
  };

  const getTopSchoolsList = () => {
    if (!data?.topSchools) return [];
    
    switch (activeTab) {
      case 'availability':
        return data.topSchools.all || [];
      case 'groups':
        // Combine all group schools
        return [
          ...data.topSchools.byGroup.army.map((s: any) => ({ ...s, groupLabel: 'Army' })),
          ...data.topSchools.byGroup.navy.map((s: any) => ({ ...s, groupLabel: 'Navy' })),
          ...data.topSchools.byGroup.airForce.map((s: any) => ({ ...s, groupLabel: 'Air Force' })),
        ].sort((a: any, b: any) => b.nccScore - a.nccScore).slice(0, 10);
      case 'wings':
        // Combine all wing schools
        return [
          ...data.topSchools.byWing.juniorDivision.map((s: any) => ({ ...s, wingLabel: 'Junior Division' })),
          ...data.topSchools.byWing.seniorDivision.map((s: any) => ({ ...s, wingLabel: 'Senior Division' })),
        ].sort((a: any, b: any) => b.nccScore - a.nccScore).slice(0, 10);
      case 'officers':
        // Combine all officer schools
        return [
          ...data.topSchools.byOfficer.ano.map((s: any) => ({ ...s, officerLabel: 'ANO' })),
          ...data.topSchools.byOfficer.cto.map((s: any) => ({ ...s, officerLabel: 'CTO' })),
        ].sort((a: any, b: any) => b.nccScore - a.nccScore).slice(0, 10);
      default:
        return data.topSchools.all || [];
    }
  };

  const getDistributionData = () => {
    if (!data) return [];
    
    switch (activeTab) {
      case 'availability':
        // For availability, show Schools with NCC vs Schools without NCC
        const totalSchools = totalSchoolsFromRedux || 0;
        const schoolsWithNCC = (data?.availability?.pmshri || 0) + (data?.availability?.nonPmshri || 0);
        const schoolsWithoutNCC = totalSchools - schoolsWithNCC;
        return [
          { name: 'Schools with NCC', value: schoolsWithNCC },
          { name: 'Schools without NCC', value: schoolsWithoutNCC },
        ];
      default:
        // For other tabs, use comparison data
        return getComparisonData().map((d: any) => ({ name: d.category, value: d.total }));
    }
  };

  const getDistributionColors = () => {
    if (activeTab === 'availability') {
      return ['#22c55e', '#ef4444']; // Green for with NCC, Red for without NCC
    }
    return undefined; // Use default colors for other tabs
  };

  const getTopSchoolsTitle = () => {
    switch (activeTab) {
      case 'availability':
        return 'Top Schools with NCC Facilities';
      case 'groups':
        return 'Top Schools by NCC Groups (Army/Navy/Air Force)';
      case 'wings':
        return 'Top Schools by NCC Wings (Junior/Senior Division)';
      case 'officers':
        return 'Top Schools by NCC Officers (ANO/CTO)';
      default:
        return 'Top Schools by NCC Infrastructure';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <div className="w-full max-w-7xl mx-auto px-4 pt-4">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] mb-2 flex items-center gap-2">
              <Shield className="w-7 h-7" />
              NCC Profile Analysis
            </h1>
            <p className="text-gray-600">
              National Cadet Corps - Groups, Wings, Officers, and Infrastructure
            </p>
          </div>
          <button
            onClick={() => navigate('/kvs-samagam/analytics')}
            className="mt-2 md:mt-0 flex items-center gap-2 px-4 py-2 border border-purple-700 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Analytics
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard
            title="Schools with NCC"
            value={(data?.summary?.totalWithNCC ?? 0).toLocaleString()}
            icon={<Shield className="w-6 h-6" />}
            color="purple"
            subtitle="NCC facilities available"
          />
          <StatCard
            title="Army Wing"
            value={(data?.summary?.totalArmy ?? 0).toLocaleString()}
            icon={<Users className="w-6 h-6" />}
            color="green"
            subtitle="Army NCC units"
          />
          <StatCard
            title="Navy Wing"
            value={(data?.summary?.totalNavy ?? 0).toLocaleString()}
            icon={<Users className="w-6 h-6" />}
            color="blue"
            subtitle="Navy NCC units"
          />
          <StatCard
            title="Air Force Wing"
            value={(data?.summary?.totalAirForce ?? 0).toLocaleString()}
            icon={<Users className="w-6 h-6" />}
            color="purple"
            subtitle="Air Force NCC units"
          />
          <StatCard
            title="NCC Officers"
            value={(data?.summary?.totalOfficers ?? 0).toLocaleString()}
            icon={<Star className="w-6 h-6" />}
            color="orange"
            subtitle="ANO + CTO"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <nav className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'text-purple-700 border-purple-600 bg-purple-50'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Charts */}
        {isLoading && activeTab === 'availability' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <ComparisonBarChart
              data={getComparisonData()}
              title={`${tabs.find(t => t.id === activeTab)?.label} - PM-SHRI vs Non PM-SHRI`}
              xAxisLabel="Category"
            />
            <DistributionPieChart
              data={getDistributionData()}
              title={`${tabs.find(t => t.id === activeTab)?.label} Distribution`}
              colors={getDistributionColors()}
            />
          </div>
        )}

        {/* Detailed Stats Table */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {tabs.find(t => t.id === activeTab)?.label} Details
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Category</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-green-600">PM-SHRI</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-blue-600">Non PM-SHRI</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-purple-600">Total</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">PM-SHRI %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {getComparisonData().map((row: any) => (
                  <tr key={row.category} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-700 font-medium">{row.category}</td>
                    <td className="py-3 px-4 text-sm text-center text-green-600 font-medium">{(row.pmshriCount ?? 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-center text-blue-600 font-medium">{(row.nonPmshriCount ?? 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-center text-purple-600 font-bold">{(row.total ?? 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-center">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        {row.total > 0 ? ((row.pmshriCount / row.total) * 100).toFixed(1) : '0.0'}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Schools List */}
        <div className="grid grid-cols-1 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-600" />
                {getTopSchoolsTitle()}
              </h3>
              <p className="text-sm text-gray-500 mt-1">Based on total cadet strength</p>
            </div>
            <div className="divide-y divide-gray-100">
              {getTopSchoolsList().length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  <Shield className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No NCC data available</p>
                </div>
              ) : (
                getTopSchoolsList().map((school: any, index: number) => (
                  <div key={school.schoolID || index} className="px-6 py-4 hover:bg-gray-50 cursor-pointer" onClick={() => school.schoolID && setSelectedSchool({ schoolID: school.schoolID, schoolName: school.schoolName || 'N/A', infraData: school, tabLabel: 'NCC Profile' })}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-gray-100 text-gray-600' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-50 text-gray-500'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-800">{school.schoolName || 'N/A'}</h4>
                            {school.status === 'PM-SHRI' && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">PM-SHRI</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{school.location || 'N/A'}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {school.groupLabel && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-semibold">
                                {school.groupLabel}
                              </span>
                            )}
                            {school.wingLabel && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-semibold">
                                {school.wingLabel}
                              </span>
                            )}
                            {school.officerLabel && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-semibold">
                                {school.officerLabel}
                              </span>
                            )}
                            {school.nccGroup && school.nccGroup !== '-' && !school.groupLabel && (
                              <span className="text-xs bg-purple-50 text-purple-600 px-2 py-1 rounded">
                                {school.nccGroup}
                              </span>
                            )}
                            {school.nccWing && school.nccWing !== '-' && !school.wingLabel && (
                              <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">
                                {school.nccWing}
                              </span>
                            )}
                            {school.officer && school.officer !== '-' && !school.officerLabel && (
                              <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded">
                                {school.officer}
                              </span>
                            )}
                            {school.totalCadets > 0 && (
                              <span className="text-xs bg-orange-50 text-orange-600 px-2 py-1 rounded">
                                {school.totalCadets} Cadets
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-purple-600">{school.nccScore ?? '-'}</div>
                        <div className="text-xs text-gray-500">Total Cadets</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Detailed Analysis Reports Section */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#6A1786] mb-4">Detailed Analysis Reports</h3>
          <p className="text-sm text-gray-600 mb-4">Access comprehensive analysis reports for each category</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Infrastructure', path: '/kvs-samagam/analytics/infrastructure', icon: '🏫', color: 'blue' },
              { name: 'Enrollment', path: '/kvs-samagam/analytics/enrollment', icon: '👨‍🎓', color: 'green' },
              { name: 'ICT Profile', path: '/kvs-samagam/analytics/ict-profile', icon: '💻', color: 'purple' },
              { name: 'Library Profile', path: '/kvs-samagam/analytics/library-profile', icon: '📚', color: 'orange' },
              { name: 'CWSN Profile', path: '/kvs-samagam/analytics/cwsn-profile', icon: '♿', color: 'pink' },
              { name: 'NCC Profile', path: '/kvs-samagam/analytics/ncc-profile', icon: '🎖️', color: 'cyan' },
              { name: 'Sports', path: '/kvs-samagam/analytics/sports', icon: '🏆', color: 'yellow' },
              { name: 'Green Building', path: '/kvs-samagam/analytics/green-building', icon: '🌿', color: 'teal' },
              { name: 'Safety', path: '/kvs-samagam/analytics/safety', icon: '🛡️', color: 'red' },
              { name: 'Special Labs', path: '/kvs-samagam/analytics/special-labs', icon: '🔬', color: 'indigo' },
              { name: 'Water Profile', path: '/kvs-samagam/analytics/miscellaneous', icon: '💧', color: 'teal' },
            ].map((item) => {
              const isCurrentPage = item.name === 'NCC Profile';
              return (
                <button
                  key={item.name}
                  onClick={() => !isCurrentPage && navigate(item.path)}
                  disabled={isCurrentPage}
                  className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 rounded-lg p-4 border border-${item.color}-200 text-center transition-all ${
                    isCurrentPage 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:shadow-lg cursor-pointer hover:scale-105'
                  }`}
                >
                  <span className="text-3xl">{item.icon}</span>
                  <p className="text-sm font-medium text-gray-700 mt-2">{item.name}</p>
                </button>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />

      {selectedSchool && (
        <SchoolDetailsModal
          schoolID={selectedSchool.schoolID}
          onClose={() => setSelectedSchool(null)}
          schoolKPIData={selectedSchool.infraData}
          selectedCategory={selectedSchool.tabLabel}
        />
      )}
    </div>
  );
}
